'use client';

import { Control } from '@velocity-exchange/icons';
import React, { useMemo, useRef } from 'react';
import TopBarIconButton from 'src/components/TopBar/TopBarIconButton';
import { DialectNotificationsButton } from 'src/components/DialectNotifications';
import Button from 'src/components/primitives/Button/Button';
import DriftTextLogo from 'src/components/Icons/DriftTextLogo';
import DesktopNav from 'src/components/Navigation/DesktopNav/DesktopNav';
import DevTopDropDown from 'src/dev/components/DevTopDropDown';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import InterfaceSettingsPopup from 'src/components/Settings/InterfaceSettings/InterfaceSettingsPopup';
import useModal from 'src/components/Modals/hooks/useModal';
import Link from 'next/link';
import ScrollShadows from 'src/components/ScrollShadows';
import TopBarWalletButton from 'src/components/TopBar/TopBarWalletButton';
import TopBarMobileNavButton from 'src/components/TopBar/TopBarMobileNavButton';
import { TOP_BAR_MOBILE_PADDING } from 'src/components/TopBar/consts';
import MobileNav from 'src/components/Navigation/MobileNav/MobileNav';
import useNavigationStore from 'src/stores/useNavigationStore';

const TopBar = () => {
	const isWalletConnected = useWalletIsConnected();
	const isMobileScreenSize = useIsMobileScreenSize();
	const isDevSwitchOn = useDevSwitchIsOn();
	const scrollableDesktopNavRef = useRef<HTMLDivElement>(null);
	const showMobileNavMenu = useNavigationStore((s) => s.showMobileNavMenu);
	const { show } = useModal();

	const showDevModal = () => {
		show({ type: 'dev_tools', props: {} });
	};

	const showDepositModal = () => {
		show({
			type: 'deposit',
			props: {
				defaultLargestBalance: true,
			},
		});
	};

	const style = useMemo(() => {
		if (!isMobileScreenSize) {
			return;
		}

		return {
			paddingTop: TOP_BAR_MOBILE_PADDING.vertical,
			paddingBottom: TOP_BAR_MOBILE_PADDING.vertical,
			paddingLeft: TOP_BAR_MOBILE_PADDING.horizontal,
			paddingRight: TOP_BAR_MOBILE_PADDING.horizontal,
		};
	}, [isMobileScreenSize]);

	return (
		<>
			<div
				id="topbar"
				className="flex grow bg-main-bg sm:px-lg sm:py-md"
				style={style}
			>
				<div className="flex w-full items-center gap-x-sm not-sm:justify-between sm:gap-x-lg">
					<Link className="shrink-0" href="/SOL-PERP">
						<DriftTextLogo />
					</Link>

					{!isMobileScreenSize && (
						<ScrollShadows
							className="min-w-0 flex-1"
							scrollableRef={scrollableDesktopNavRef}
							gradientFromClassName="from-main-bg"
							screenSize="all"
						>
							<div
								className="hidden-scroll overflow-x-auto"
								ref={scrollableDesktopNavRef}
							>
								<DesktopNav />
							</div>
						</ScrollShadows>
					)}

					<div className="relative flex h-full shrink-0 items-center gap-x-xs sm:gap-x-sm">
						{isDevSwitchOn && (
							<TopBarIconButton onClick={showDevModal} icon={Control} />
						)}

						{!isMobileScreenSize && isWalletConnected && (
							<Button size="md" variant="stroke" onClick={showDepositModal}>
								Deposit
							</Button>
						)}

						<TopBarWalletButton />

						{!isMobileScreenSize && (
							<>
								{Env.dialectNotificationsEnabled && isWalletConnected && (
									<DialectNotificationsButton />
								)}
								<InterfaceSettingsPopup />
							</>
						)}

						{isMobileScreenSize && (
							<>
								<InterfaceSettingsPopup />
								{Env.dialectNotificationsEnabled && (
									<DialectNotificationsButton />
								)}
								<TopBarMobileNavButton />
								{showMobileNavMenu && <MobileNav />}
							</>
						)}
					</div>
				</div>
			</div>

			{isDevSwitchOn && !isMobileScreenSize && (
				<div className="absolute top-0 left-1/2">
					<DevTopDropDown />
				</div>
			)}
		</>
	);
};

export default React.memo(TopBar);
